import _ from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Layout } from 'blaze-validator';
import browserHistory from '../../history';
import toastr from '../toastr';
import LoadingPage from '../LoadingPage';
import Form from '../form/Form';
import { getInfo } from '../../actions/AppActions';
import { modal } from '../modal/ModalEmitter';
import ApiService from '../../services/ApiService';
import FileService from '../../services/FileService';
import PdfObject from '../PdfObject';
import PageService from '../../services/PageService';
import SocketService from '../../services/SocketService';
import AuthService from '../../services/AuthService';
import FileNotFound from '../FileNotFound';

// Save or Submit label
const useSaveLabel = ['EQS1'];

const formatDbValues = (dbItem, layout, action) => {
  const layoutObj = new Layout(layout);
  const layoutLookup = layoutObj.getLookup();

  const flattenItem = {};
  Object.keys(layoutLookup).forEach(fieldName => {
    const value = _.get(dbItem, fieldName);

    if (Array.isArray(value)) {
      if (typeof value[0] !== 'object' || value[0] === null) {
        flattenItem[fieldName] = value;
        return;
      }

      // here we are assuming an array of objects
      // the id key will be used for data, and displayName or name will be used for dataAlt
      flattenItem[fieldName] = {
        data: _.map(value, _.property('id')),
        dataAlt: _.map(value, params => {
          if (!params) {
            return '';
          }

          return params.displayName || params.name;
        }),
      };

      return;
    }

    if (value && _.isObject(value)) {
      flattenItem[fieldName] = {
        data: value.id,
        dataAlt: value.displayName || value.name,
      };

      return;
    }

    flattenItem[fieldName] = value;
  });

  let conditional;

  if (action === 'copy') {
    conditional = field => field.noCopy !== true;
  }

  return layoutObj.populateLayout(flattenItem, conditional).getLayout();
};

const DbEditContainer = ({
  action,
  customActions = [],
  itemId,
  itemName,
  onCancel,
  onChange,
  page,
  persistTabs,
  query,
  readOnly,
  submitCallback,
  usePrefix,
  ...rest
}) => {
  const dispatch = useDispatch();
  const config = useSelector(state => state.app.config);
  const { user } = useSelector(state => state.app);

  const [locked, setLocked] = useState(false);
  const [meta, setMeta] = useState({}); // { itemId, page, pageLayout }
  const [dbItem, setDbItem] = useState({});
  const [layout, setLayout] = useState([]);
  const [hideMfa, setHideMfa] = useState(false);
  const [options, setOptions] = useState([]);

  const [key, setKey] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchDbItem = () => {
    return ApiService.get(`/db/${itemName}/${itemId}`).then(response => {
      setDbItem(response.data);
      return Promise.resolve(response.data);
    });
  };

  useEffect(() => {
    if (_.get(page, 'Options')) {
      setOptions(page.Options.map(o => o.toLowerCase()));
    } else {
      setOptions([]);
    }
  }, []);

  const fetchMeta = () => {
    return ApiService.get(`/db/${itemName}/describe`).then(response => {
      setMeta(response.data.meta);

      // TODO Probably don't need separate state for this
      if (_.get(response.data.meta, 'yaml.page.Options')) {
        setOptions(_.get(response.data.meta, 'yaml.page.Options').map(o => o.toLowerCase()));
      }

      return Promise.resolve(response.data.meta);
    });
  };

  const isOption = (name = '') => {
    return options.includes(name.toLowerCase());
  };

  const hasItemId = () => {
    if (layout[0] && layout[0].name === 'id') {
      return true;
    }

    return false;
  };

  const getItemId = () => {
    const itemIdMeta = meta.itemId || {};

    itemIdMeta.data = itemId;

    if (hasItemId()) {
      return { ...itemIdMeta, ...layout[0] };
    }

    itemIdMeta.disabled = true;
    itemIdMeta.hidden = true;

    return itemIdMeta;
  };

  const updateItem = payload => {
    // transform payload into simple key: value json
    const updateData = {};
    for (const field of payload) {
      updateData[field.name] = field.data;
    }

    return ApiService.put(`/db/${itemName}/${itemId}`, updateData);
  };

  const createItem = payload => {
    // transform payload into simple key: value json
    const insertData = {};
    for (const field of payload) {
      insertData[field.name] = field.data;
    }

    return ApiService.post(`/db/${itemName}`, insertData);
  };

  const handlePermissionButton = () => {
    browserHistory.push(`/db/UST3R/${itemId}/edit`);
    return true;
  };

  const renderPdf = ({ manager }) => {
    const filename = manager.getValue('filename').filename;
    if (!filename) {
      toastr.error('Could not load preview, was it saved?');
      return;
    }

    if (!filename.endsWith('.pdf')) {
      modal.alert('Preview only supported for PDF templates at the moment');
      return;
    }

    const pdfUrl = FileService.downloadPath(filename, 'templates');

    modal.open({
      title: 'Template Preview',
      message: <PdfObject url={pdfUrl} width="100%" height="800" />,
    });
  };

  const getCustomActions = () => {
    if (itemName === 'UST3') {
      if (action === 'edit') {
        const actions = [
          {
            label: 'Permissions',
            action: handlePermissionButton,
            style: 'info',
          },
        ];

        if (dbItem.mfaSecret && !hideMfa) {
          actions.push({
            label: 'Remove MFA Token',
            action: () => {
              modal.confirm({
                message: 'This will remove multi-factor authentication for this user.',
                callback: () => {
                  ApiService.post('auth/mfa/disable', null, { params: { userId: itemId } })
                    .then(() => {
                      // used to remove button from view.
                      setHideMfa(true);
                      toastr.success('MFA Token Removed');
                    })
                    .catch(err => {
                      toastr.error(err.message);
                    });
                },
              });
            },
            style: 'info',
          });
        }

        if (config.features.chat) {
          actions.push({
            label: 'Clear Chat History',
            style: 'danger',
            action: () => {
              modal.confirm({
                message: 'This will clear all chat messages sent to and received by this user.',
                callback: () => {
                  ApiService.delete(`chat/user/${itemId}`)
                    .then(() => toastr.success('Chat History Cleared'))
                    .catch(err => toastr.error(err.message));
                },
              });
            },
          });
        }

        return actions;
      }
    }

    if (itemName === 'UPT1' && itemId) {
      return [
        {
          label: 'Preview',
          action: renderPdf,
          style: 'info',
        },
      ];
    }

    return customActions;
  };

  const handleRedirect = () => {
    if (itemName === 'UST3R') {
      browserHistory.push(`/list/${itemName}/edit`);
    } else if (itemName === 'EQS1') {
      browserHistory.goBack();
    } else {
      browserHistory.push(`/db/${itemName}`);
    }
  };

  const handleChange = (...args) => {
    if (!isCreationAction() && !locked) {
      PageService.lockPage(itemName, itemId);
    }

    if (onChange) {
      onChange(...args);
    }
  };

  const createCallback = response => {
    const itemId = _.get(response, 'data.id');

    if (itemName === 'UST3') {
      if (itemId) {
        browserHistory.push(`/db/UST3R/${itemId}/edit?new`);
      }
    } else {
      handleRedirect();
    }
  };

  const updateCallback = () => {
    // Get app info for Branch updates
    if (itemName === 'UST2') {
      dispatch(getInfo());
    }

    handleRedirect();
  };

  /**
   * Determines what type of action the form is making.
   *
   * @return {Boolean}
   */
  const isCreationAction = () => {
    if ((itemId && itemId === '_new') || action === 'new' || action === 'copy') {
      return true;
    }

    return false;
  };

  const showLockedDialog = (lockedBy = {}) => {
    modal.open('pageLocked', {
      id: 'page-lock',
      hidePrint: true,
      render: 'pageLocked',
      style: 'center',
      title: 'Notice',
      confirm: true,
      size: 'md',
      confirmLabels: ['Force Unlock', 'Close'],
      callback: () => {
        PageService.takeLock(itemName, itemId).then(() => {
          refresh();
        });
      },
      lockedBy,
    });
  };

  const checkForLock = () => {
    PageService.getLock(itemName, itemId).then(response => {
      const lockedBy = response.data.user;

      if (!lockedBy) {
        setLocked(false);
        return;
      }

      if (lockedBy.username === user.username) {
        setLocked(false);
        return;
      }

      setLocked(true);
      showLockedDialog(lockedBy);
    });
  };

  const refresh = () => {
    setLoading(true);
    if (action !== 'new') {
      Promise.all([fetchMeta(), fetchDbItem()]).then(values => {
        const meta = values[0];
        const dbItem = values[1];
        setLayout(formatDbValues(dbItem, meta.pageLayout, action));
        setKey(key => key + 1); // TODO This is only needed because data/layout changes will not be reflected on blazeform rerenders currently
        checkForLock();
        setLoading(false);
      });
    } else {
      fetchMeta().then(response => {
        setLayout(response.pageLayout);
        setLoading(false);
      });
    }
  };

  useEffect(() => {
    refresh();
    const lockId = `${user.branch.mvAccount}-${itemName}-${itemId}`;

    SocketService.global.on(`${lockId}.lock`, (lockedBy = {}) => {
      if (!locked && user.id !== lockedBy.id) {
        setLocked(true);
        showLockedDialog(lockedBy);
      }
    });

    return () => {
      PageService.unlockMyPage(itemName, itemId);
      SocketService.global.off(`${lockId}.lock`);
    };
  }, []);

  if (!AuthService.hasPermission(itemName)) {
    return <FileNotFound>Missing permission for {itemName}</FileNotFound>;
  }

  if (loading || !layout || layout.length === 0) {
    return <LoadingPage />;
  }

  const isReadOnly = isOption('ReadOnly') || readOnly || locked;

  return (
    <Form
      key={key}
      {...rest}
      action={action}
      customActions={getCustomActions()}
      className="fill"
      initialValues={query}
      itemId={getItemId()}
      itemName={itemName}
      layout={layout}
      onCancel={
        onCancel ||
        (() => {
          browserHistory.goBack();
        })
      }
      onChange={handleChange}
      page={meta.page}
      persistTabs={persistTabs}
      readOnly={isReadOnly}
      submit={isCreationAction() ? createItem : updateItem}
      submitAll={isOption('SubmitAll')}
      submitCallback={isCreationAction() ? submitCallback || createCallback : submitCallback || updateCallback}
      submitLabel={useSaveLabel.includes(itemName) ? 'Save' : 'Submit'}
      trackDirty={!isCreationAction()}
      usePrefix={usePrefix}
      useTypedData
    />
  );
};

DbEditContainer.defaultProps = {
  itemId: '_new',
  query: {},
};

DbEditContainer.propTypes = {
  action: PropTypes.string,
  itemId: PropTypes.string,
  itemName: PropTypes.string,
  onCancel: PropTypes.func,
  persistTabs: PropTypes.bool,
  query: PropTypes.object,
  submitCallback: PropTypes.func,
  usePrefix: PropTypes.string,
};

export default DbEditContainer;
